import type { Variant } from '../types/types';

/** Decodes Shopify GID (e.g. gid://shopify/Product/5290935779465) and parse it's numeric id. */
export function getProductIdFromShopifyId(shopifyId: string) {
  const pieces = shopifyId.split('/');
  return pieces[pieces.length - 1];
}

/**
 * Returns the variant of a product corresponding to the options given.
 *
 * @example
 * const selectedVariant = variantForOptions(product, {
 *   size: "Small",
 *   color: "Red"
 * });
 *
 * @param {GraphModel} variants Variants to find the appropriate one from.
 * @param {Object} options An object containing the options for the variant.
 * @return {GraphModel} The variant corresponding to the options given.
 */
export function variantForOptions(
  variants: Variant[],
  options: Record<string, string | null>
) {
  return variants.find(variant => {
    return variant.selectedOptions.every(selectedOption => {
      return options[selectedOption.name] === selectedOption.value.valueOf();
    });
  });
}

/**
 * Returns the values of an option (e.g. the sizes) that can't be bought: no available variant has them, in
 * combination with the values already selected for the other options (if any).
 *
 * @example
 * // ['S'] if all the S variants are sold out
 * unavailableOptionValues(variants, sizeOption, { Size: null }, variant => variant.availableForSale);
 */
export function unavailableOptionValues(
  variants: Variant[],
  option: { name: string; values: string[] },
  selectedOptions: Record<string, string | null>,
  isVariantAvailable: (variant: Variant) => boolean
) {
  return option.values.filter(
    value =>
      !variants.some(
        variant =>
          isVariantAvailable(variant) &&
          variant.selectedOptions.every(({ name, value: variantValue }) =>
            name === option.name ?
              variantValue === value
              // other options only restrict it once a value is selected for them
            : selectedOptions[name] == null ||
              selectedOptions[name] === variantValue
          )
      )
  );
}
